import { toUtcMs } from './dateTimeUtils';

/**
 * Format date to DD/MMM/YYYY format
 * @param {Date|Timestamp|number} date - Date object, Firestore Timestamp, or milliseconds
 * @returns {string} Formatted date string (e.g., "15/Jan/2024")
 */
export function formatDate(date) {
  const ms = toUtcMs(date);
  if (ms == null) return 'N/A';
  const dateObj = new Date(ms);
  const day = String(dateObj.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format timestamp to locale time string
 * @param {Date|Timestamp|number} date - Date object, Firestore Timestamp, or milliseconds
 * @returns {string} Formatted time string in device locale
 */
export function formatTime(date) {
  const ms = toUtcMs(date);
  if (ms == null) return 'N/A';
  return new Date(ms).toLocaleTimeString();
}

/**
 * Format date and time to DD/MMM/YYYY HH:MM format
 * @param {Date|Timestamp|number} date - Date object, Firestore Timestamp, or milliseconds
 * @returns {string} Formatted date and time string
 */
export function formatDateTime(date) {
  const ms = toUtcMs(date);
  if (ms == null) return 'N/A';
  const dateObj = new Date(ms);
  const dateStr = formatDate(ms);
  const timeStr = dateObj.toLocaleTimeString();
  return `${dateStr} ${timeStr}`;
}

/**
 * Format match scheduled date only (no time from timestamp).
 * Use with scheduledTime for display to match schedule.
 */
export function formatScheduledDate(scheduledDate) {
  return formatDate(scheduledDate);
}/**
 * Format match scheduled time. Prefer scheduledTime (HH:MM from schedule) over timestamp.
 * @param {Date|Timestamp|number} scheduledDate - Match scheduled date
 * @param {string} [scheduledTime] - Match scheduled time (e.g. "18:30")
 */
export function formatScheduledTime(scheduledDate, scheduledTime) {
  if (scheduledTime && typeof scheduledTime === 'string') return scheduledTime.trim() || 'N/A';
  return scheduledDate ? formatTime(scheduledDate) : 'N/A';
}/**
 * Format match date + time for display. Uses scheduled date and scheduled time (same as match schedule).
 * @param {Date|Timestamp|number} scheduledDate - Match scheduled date
 * @param {string} [scheduledTime] - Match scheduled time (e.g. "18:30")
 * @param {{ useSeparator?: string }} [opts] - e.g. { useSeparator: ' at ' }
 */
export function formatScheduledMatchDateTime(scheduledDate, scheduledTime, opts = {}) {
  const sep = opts.useSeparator ?? ', ';
  if (!scheduledDate) return 'N/A';
  const d = formatScheduledDate(scheduledDate);
  const t = formatScheduledTime(scheduledDate, scheduledTime);
  return t !== 'N/A' ? `${d}${sep}${t}` : d;
}

/**
 * Get date-only string YYYY-MM-DD from scheduledDate (for filtering/comparison).
 * @param {Date|Timestamp|{ seconds: number }|string} scheduledDate
 * @returns {string} 'YYYY-MM-DD' or ''
 */
export function getScheduledDateKey(scheduledDate) {
  if (!scheduledDate) return '';
  let ms = toUtcMs(scheduledDate);
  if (ms == null && typeof scheduledDate === 'string') {
    const parsed = new Date(scheduledDate);
    ms = isNaN(parsed.getTime()) ? null : parsed.getTime();
  }
  if (ms == null) return '';
  const dateObj = new Date(ms);
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Sort comparator: chronological (earliest first) by scheduled date/time.
 * Prefer scheduledAt (UTC ms); fallback to scheduledDate.
 */
export function sortMatchesChronologically(a, b) {
  const aMs = toUtcMs(a.scheduledAt) ?? toUtcMs(a.scheduledDate) ?? 0;
  const bMs = toUtcMs(b.scheduledAt) ?? toUtcMs(b.scheduledDate) ?? 0;
  const d = aMs - bMs;
  if (d !== 0) return d;
  return (a.scheduledTime || '').localeCompare(b.scheduledTime || '');
}
